import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { useNetworkStore } from "@/features/network-topology/public";
import {
  autoRouteBetween,
  createDefaultScene,
  deserializeScene,
  type FactoryScene,
  nextObjectId,
  type ObjectKind,
  type SceneObject,
  type SceneValidationIssue,
  serializeScene,
  useSimulationStore,
  validateScene,
} from "@/features/simulation/public";
import type { TilePos } from "@/lib/pixi/isoCoords";

/**
 * 編集中の sim オブジェクト追加/削除を network 側に伝搬するマップ (P5)。
 *  - 'press' → 'plc'
 *  - 'arm' → 'robot_controller'
 *  - 'transport' → 'agv_controller'
 *  - 'conveyor' / 'sensor' は network 連動なし
 *
 * network ノードの id は `sim-${objectId}` 形式で対応 (削除時の検索用)。
 * 親は固定で `l2-line-a` (将来の自動配線で変更想定)。
 */
const SIM_TO_NETWORK_KIND: Partial<Record<ObjectKind, "plc" | "robot_controller" | "agv_controller">> = {
  press: "plc",
  arm: "robot_controller",
  transport: "agv_controller",
};

const NETWORK_PARENT_ID = "l2-line-a";

function syncEditorAddToNetwork(kind: ObjectKind, objectId: string): void {
  const networkKind = SIM_TO_NETWORK_KIND[kind];
  if (!networkKind) return;
  if (kind !== "press" && kind !== "arm" && kind !== "transport") return;
  useNetworkStore.getState().addNode({
    id: `sim-${objectId}`,
    kind: networkKind,
    label: objectId,
    parentId: NETWORK_PARENT_ID,
    simBinding: { kind },
    linkType: "rj45",
  });
}

function syncEditorRemoveFromNetwork(objectId: string): void {
  // 存在しなくても store 側で no-op
  useNetworkStore.getState().removeNode(`sim-${objectId}`);
}

/**
 * シーンエディタ用 Zustand ストア。
 *
 * - sim 側 store とは独立した「ドラフト」シーンを保持する
 * - 「保存」ボタン押下時に `useSimulationStore.setScene(draft)` で反映
 * - localStorage に draft を永続化 (リロード後も編集途中が残る)
 * - JSON Import/Export
 *
 * 単一選択モデル: 1 オブジェクトのみ選択中状態 (kind+id) を保持。
 */

export type SelectionRef = { kind: ObjectKind; id: string } | null;

/** パレット: 配置モード時に「次にどの kind を置くか」を保持 */
export type PlacementMode = ObjectKind | null;

interface EditorStoreState {
  draft: FactoryScene;
  /** 選択中オブジェクト */
  selection: SelectionRef;
  /** パレットで選択中の配置 kind (null なら配置モードでない) */
  placement: PlacementMode;
  /**
   * 搬送ロボの経路編集モード (P4)。
   * クリックされたタイルを partial に順次追加していく。
   */
  routeEditing: { transportId: string; partial: TilePos[] } | null;
  /** 検証結果のキャッシュ (draft 更新時に再計算) */
  issues: SceneValidationIssue[];
}

interface EditorStoreActions {
  /** sim store の現在 scene をドラフトに引き込む (ページ初期表示時に呼ぶ) */
  loadFromSim(): void;
  /** ドラフトを sim store へ反映 (保存ボタン) */
  applyToSim(): void;
  /** ドラフトを破棄 (sim の現状を再ロード) */
  discard(): void;

  setSelection(sel: SelectionRef): void;
  setPlacement(mode: PlacementMode): void;

  /** タイル位置に新規オブジェクトを配置 (placement モード時) */
  placeObject(kind: ObjectKind, tile: TilePos): string | null;
  /** 既存オブジェクトの位置を更新 (ドラッグ移動など) */
  moveObject(ref: { kind: ObjectKind; id: string }, tile: TilePos): void;
  /** プロパティパネルからの単純な書き換え */
  updateObject(ref: { kind: ObjectKind; id: string }, patch: Partial<Omit<SceneObject, "id" | "kind">>): void;
  /** オブジェクト削除 */
  removeObject(ref: { kind: ObjectKind; id: string }): void;

  /** JSON 文字列を draft に読み込む (失敗時は throw) */
  importJson(json: string): void;
  /** draft を JSON 文字列で取得 */
  exportJson(): string;

  /** draft を既定構成にリセット */
  resetDraft(): void;

  /** 搬送ロボの経路編集モードを開始 (P4)。parking タイルを最初の waypoint にする */
  beginRouteEdit(transportId: string): void;
  /** 経路編集モード中に waypoint を追加 (canvas クリック由来) */
  appendRouteWaypoint(tile: TilePos): void;
  /** 経路編集を確定: 対象 transport の route を上書き、routeAutoMode=false */
  finishRouteEdit(): void;
  /** 経路編集をキャンセル */
  cancelRouteEdit(): void;
}

export type EditorStore = EditorStoreState & EditorStoreActions;

function pickArray(scene: FactoryScene, kind: ObjectKind): SceneObject[] {
  switch (kind) {
    case "conveyor":
      return scene.conveyors as unknown as SceneObject[];
    case "press":
      return scene.presses as unknown as SceneObject[];
    case "arm":
      return scene.arms as unknown as SceneObject[];
    case "transport":
      return scene.transports as unknown as SceneObject[];
    case "sensor":
      return scene.sensors as unknown as SceneObject[];
  }
}

function withMutatedArray(scene: FactoryScene, kind: ObjectKind, next: SceneObject[]): FactoryScene {
  switch (kind) {
    case "conveyor":
      return { ...scene, conveyors: next as FactoryScene["conveyors"] };
    case "press":
      return { ...scene, presses: next as FactoryScene["presses"] };
    case "arm":
      return { ...scene, arms: next as FactoryScene["arms"] };
    case "transport":
      return { ...scene, transports: next as FactoryScene["transports"] };
    case "sensor":
      return { ...scene, sensors: next as FactoryScene["sensors"] };
  }
}

/** kind + tile から新規オブジェクトの初期値を作る */
function createNewObject(scene: FactoryScene, kind: ObjectKind, tile: TilePos): SceneObject {
  const id = nextObjectId(scene, kind);
  switch (kind) {
    case "conveyor":
      return {
        id,
        kind: "conveyor",
        path: [tile, { col: tile.col + 1, row: tile.row }],
        isEntry: scene.conveyors.length === 0,
        bufferCapacity: 3,
      };
    case "press": {
      const conveyorId = scene.conveyors[0]?.id ?? "conveyor-1";
      return { id, kind: "press", position: tile, conveyorId };
    }
    case "arm":
      return {
        id,
        kind: "arm",
        position: tile,
        pickFromTile: { col: tile.col - 1, row: tile.row },
        placeToTile: { col: tile.col, row: tile.row + 1 },
      };
    case "transport": {
      const unloading = { col: tile.col, row: tile.row + 5 };
      return {
        id,
        kind: "transport",
        parking: tile,
        unloading,
        route: autoRouteBetween(tile, unloading),
        routeAutoMode: true,
        capacity: 3,
      };
    }
    case "sensor":
      return { id, kind: "sensor", position: tile };
  }
}

export const useEditorStore = create<EditorStore>()(
  devtools(
    persist(
      (set, get) => {
        const recompute = (draft: FactoryScene) => ({
          draft,
          issues: validateScene(draft),
        });

        return {
          draft: createDefaultScene(),
          selection: null,
          placement: null,
          routeEditing: null,
          issues: [],

          loadFromSim() {
            const simScene = useSimulationStore.getState().scene;
            set({ ...recompute(simScene), selection: null, placement: null }, false, "editor/loadFromSim");
          },

          applyToSim() {
            const { draft } = get();
            useSimulationStore.getState().setScene(draft);
          },

          discard() {
            get().loadFromSim();
          },

          setSelection(sel) {
            set({ selection: sel, placement: null }, false, "editor/setSelection");
          },

          setPlacement(mode) {
            set({ placement: mode, selection: null }, false, "editor/setPlacement");
          },

          placeObject(kind, tile) {
            const { draft } = get();
            const arr = pickArray(draft, kind);
            const obj = createNewObject(draft, kind, tile);
            const next = withMutatedArray(draft, kind, [...arr, obj]);
            set({ ...recompute(next), selection: { kind, id: obj.id }, placement: null }, false, "editor/placeObject");
            // P5: sim 連動の kind なら network ノードも自動追加
            syncEditorAddToNetwork(kind, obj.id);
            return obj.id;
          },

          moveObject(ref, tile) {
            const { draft } = get();
            const arr = pickArray(draft, ref.kind);
            const updated = arr.map((o): SceneObject => {
              if (o.id !== ref.id) return o;
              if (o.kind === "conveyor") {
                // path 全体を平行移動
                const offset = { col: tile.col - (o.path[0]?.col ?? 0), row: tile.row - (o.path[0]?.row ?? 0) };
                const path = o.path.map((p) => ({ col: p.col + offset.col, row: p.row + offset.row }));
                return { ...o, path };
              }
              if (o.kind === "press" || o.kind === "sensor") {
                return { ...o, position: tile };
              }
              if (o.kind === "arm") {
                // pickFrom / placeTo を相対関係保ったまま追従
                const dx = tile.col - o.position.col;
                const dy = tile.row - o.position.row;
                return {
                  ...o,
                  position: tile,
                  pickFromTile: { col: o.pickFromTile.col + dx, row: o.pickFromTile.row + dy },
                  placeToTile: { col: o.placeToTile.col + dx, row: o.placeToTile.row + dy },
                };
              }
              // transport
              const dx = tile.col - o.parking.col;
              const dy = tile.row - o.parking.row;
              return {
                ...o,
                parking: tile,
                unloading: { col: o.unloading.col + dx, row: o.unloading.row + dy },
                route: o.route.map((p) => ({ col: p.col + dx, row: p.row + dy })),
              };
            });
            const next = withMutatedArray(draft, ref.kind, updated);
            set(recompute(next), false, "editor/moveObject");
          },

          updateObject(ref, patch) {
            const { draft } = get();
            const arr = pickArray(draft, ref.kind);
            const updated = arr.map((o) => (o.id === ref.id ? ({ ...o, ...patch } as SceneObject) : o));
            const next = withMutatedArray(draft, ref.kind, updated);
            set(recompute(next), false, "editor/updateObject");
          },

          removeObject(ref) {
            const { draft, selection } = get();
            const arr = pickArray(draft, ref.kind);
            const next = withMutatedArray(
              draft,
              ref.kind,
              arr.filter((o) => o.id !== ref.id),
            );
            const cleared = selection?.kind === ref.kind && selection.id === ref.id ? null : selection;
            set({ ...recompute(next), selection: cleared }, false, "editor/removeObject");
            // P5: 対応する network ノードも自動削除
            syncEditorRemoveFromNetwork(ref.id);
          },

          importJson(json) {
            const scene = deserializeScene(json);
            set({ ...recompute(scene), selection: null, placement: null }, false, "editor/importJson");
          },

          exportJson() {
            return serializeScene(get().draft);
          },

          resetDraft() {
            const scene = createDefaultScene();
            set(
              { ...recompute(scene), selection: null, placement: null, routeEditing: null },
              false,
              "editor/resetDraft",
            );
          },

          beginRouteEdit(transportId) {
            const t = get().draft.transports.find((x) => x.id === transportId);
            if (!t) return;
            set(
              {
                routeEditing: { transportId, partial: [t.parking] },
                placement: null,
              },
              false,
              "editor/beginRouteEdit",
            );
          },

          appendRouteWaypoint(tile) {
            const re = get().routeEditing;
            if (!re) return;
            // 直前の waypoint と同じタイルは追加しない
            const last = re.partial[re.partial.length - 1];
            if (last && last.col === tile.col && last.row === tile.row) return;
            set(
              { routeEditing: { transportId: re.transportId, partial: [...re.partial, tile] } },
              false,
              "editor/appendRouteWaypoint",
            );
          },

          finishRouteEdit() {
            const re = get().routeEditing;
            if (!re || re.partial.length < 2) {
              set({ routeEditing: null }, false, "editor/finishRouteEdit/abort");
              return;
            }
            const last = re.partial[re.partial.length - 1];
            const t = get().draft.transports.find((x) => x.id === re.transportId);
            if (!last || !t) {
              set({ routeEditing: null }, false, "editor/finishRouteEdit/missing");
              return;
            }
            // 末尾を unloading として更新、route も新しい partial を採用
            const arr = pickArray(get().draft, "transport").map((o) => {
              if (o.id !== re.transportId) return o;
              if (o.kind !== "transport") return o;
              return { ...o, route: re.partial, unloading: last, routeAutoMode: false };
            });
            const next = withMutatedArray(get().draft, "transport", arr);
            set({ ...recompute(next), routeEditing: null }, false, "editor/finishRouteEdit");
          },

          cancelRouteEdit() {
            set({ routeEditing: null }, false, "editor/cancelRouteEdit");
          },
        };
      },
      {
        name: "factory-twin-editor-draft",
        partialize: (s) => ({ draft: s.draft }),
      },
    ),
    { name: "EditorStore" },
  ),
);
